import { getTableDrillDownLink } from "@components/drillDown/DrillDown.utils";
import { IFieldInfoProperties } from "@components/smart/FieldInfo";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IReportHierarchy } from "@components/smart/smartTable";
import { TFormatterFn } from "@components/smart/smartTable/SmartTable.utils";
import BindingContext from "@odata/BindingContext";
import { CommonReportProps } from "@pages/reports/CommonDefs";
import {
    composedDateRangeOnBeforeLoadCallback,
    getComposedDateRangeFieldsDefs
} from "@pages/reports/customFilterComponents/ComposedDateRange";
import {
    getEmployeeNumberOursCommonDef,
    getPayslipCommonDef,
    payslipInitialSortBy
} from "@pages/reports/payrollOverview/PayrollOverview.utils";
import { setReportDef } from "@pages/reports/Report.test.utils";
import { ReportStorage } from "@pages/reports/ReportStorage";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import { isDefined } from "@utils/general";
import i18next from "i18next";

import { BENEFIT_OVERVIEW_ROUTE, DEDUCTION_OVERVIEW_ROUTE } from "../../../routes";
import { formatCurrency } from "../../../types/Currency";
import { IReportTableDefinition, TReportColumnOverrides } from "../Report.utils";
import { ReportId } from "../ReportIds";

function getCommonDrilldownToOtherPRReports(router: string): TFormatterFn {
    return (val, args) => {
        const formattedVal = formatCurrency(val, getCompanyCurrency(args.context));
        if (val === 0 || !isDefined(val)) {
            return formattedVal;
        }

        const filters = {
            [CommonReportProps.dateRange]: args.storage.data.entity[CommonReportProps.dateRange],
            [CommonReportProps.dateRangeCustomValue]: args.storage.data.entity[CommonReportProps.dateRangeCustomValue],
            [BindingContext.localContext("PrEmployee_NumberOurs")]: args.entity["PrEmployee_NumberOurs"],
            [BindingContext.localContext("YearMonth")]: args.entity["YearMonth"],
        };

        return getTableDrillDownLink(formattedVal, {
            route: router,
            context: args.storage.context,
            storage: args.storage,
            filters
        });
    };
}

export const getDefinition = (): IReportTableDefinition => {
    const tableId = ReportId.PayrollOverview;
    const title = i18next.t("Reporting:PayrollOverview.Title");
    const path = "PayrollOverview";

    const filterBarDef: IFilterGroupDef[] = [{
        ...getDefaultFilterGroupDef(FilterBarGroup.Parameters),
        defaultFilters: [
            CommonReportProps.dateRange,
            CommonReportProps.dateRangeCustomValue,
        ],
        filterDefinition: {
            ...getComposedDateRangeFieldsDefs(tableId, true),
        },
    }, {
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        allowCustomFilters: false,
        defaultFilters: [],
        filterDefinition: {}
    }];

    const onBeforeLoad = async (storage: ReportStorage) => {
        await composedDateRangeOnBeforeLoadCallback(storage, false, true, true);
    };

    const parameters: string[] = [CommonReportProps.dateRange, CommonReportProps.dateRangeCustomValue];
    const defaultReportHierarchy: IReportHierarchy = {
        "Aggregate": false,
        "Groups": [],
        "Columns": [
            {
                "ColumnAlias": "YearMonth",
            },
            {
                "ColumnAlias": "PrEmployee_NumberOurs",
            },
            {
                "ColumnAlias": "Name",
            },
            {
                "ColumnAlias": "PrPayslipEmploymentItem_CzGrossSalaryTotal"
            },
            {
                "ColumnAlias": "PrPayslipEmploymentItem_CzAbsencePaidTotal"
            },
            {
                "ColumnAlias": "PrPayslipEmploymentItem_CzExtraSalaryTotal"
            },
            {
                "ColumnAlias": "PrPayslipItem_CzBenefitsTotal",
            },
            {
                "ColumnAlias": "PrPayslipEmploymentItem_CzSavingForRetiremTotal",
            },
            {
                "ColumnAlias": "PrPayslipEmploymentItem_CzEmployeeCar",
            },
            {
                "ColumnAlias": "PrPayslipItem_CzHealthInsuranceEmployee",
            },
            {
                "ColumnAlias": "PrPayslipItem_CzSocialInsuranceEmployee",
            },
            {
                "ColumnAlias": "PrPayslipItem_CzIncomeTaxNetAdvance",
            },
            {
                "ColumnAlias": "PrPayslipItem_CzIncomeTaxNetWithholding",
            },
            {
                "ColumnAlias": "PrPayslipItem_CzIncomeTaxBonus",
            },
            {
                "ColumnAlias": "PrPayslipItem_CzIncomeTaxAdvantage",
            },
            {
                "ColumnAlias": "PrPayslipItem_CzIncomeTaxReduction",
            },
            {
                "ColumnAlias": "PrPayslipItem_CzDeductionTotal",
            },
            {
                "ColumnAlias": "PrPayslipItem_CzSalaryAdvanceSubtr",
            },
            {
                "ColumnAlias": "PrPayslipItem_CzNetSalary",
            },
            {
                "ColumnAlias": "PrPayslipItem_OtherIncome",
            },
            {
                "ColumnAlias": "PrPayslipItem_CzIllnessTotal",
            },
            {
                "ColumnAlias": "PrPayslipItem_CzAmountToPayEmployee",
            },
            {
                "ColumnAlias": "PrPayslipItem_CzHealthInsuranceEmployer",
            },
            {
                "ColumnAlias": "PrPayslipItem_CzSocialInsuranceEmployer",
            }
        ],
        "Aggregations": []
    };

    const columnOverrides: TReportColumnOverrides = (columnId: string): IFieldInfoProperties => {
        if (columnId === "YearMonth") {
            return getPayslipCommonDef();
        }

        if (columnId === "PrEmployee_NumberOurs") {
            return getEmployeeNumberOursCommonDef();
        }

        if (columnId.startsWith("PrPayslipItem_CzBenefitsTotal")) {
            return {
                formatter: getCommonDrilldownToOtherPRReports(BENEFIT_OVERVIEW_ROUTE)
            };
        }

        if (columnId.startsWith("PrPayslipItem_CzDeductionTotal")) {
            return {
                formatter: getCommonDrilldownToOtherPRReports(DEDUCTION_OVERVIEW_ROUTE)
            };
        }

        return null;
    };

    const initialSortBy = payslipInitialSortBy;

    return {
        title,
        path,
        id: tableId,
        columnOverrides,
        initialSortBy,
        filterBarDef,
        onBeforeLoad,
        parameters,
        defaultReportHierarchy
    };
};

setReportDef(ReportId.PayrollOverview, getDefinition);